import React, { useState } from 'react';
import './SheetsImport.css';
import baseURL from '../../url';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faEye, faSync } from '@fortawesome/free-solid-svg-icons';
import { clearResourceCache } from '../../storeData';

const DEFAULT_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1eQUWQ7iW36F3l5uojvZcZKr_mXMXEVnr29_LVHQ0IyM/edit';
const DEFAULT_PRODUCTOS_GID = '1359024946';

export default function SheetsImport() {
    const [spreadsheetUrl, setSpreadsheetUrl] = useState(DEFAULT_SHEET_URL);
    const [gid, setGid] = useState(DEFAULT_PRODUCTOS_GID);
    const [preview, setPreview] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingImport, setLoadingImport] = useState(false);
    const [open, setOpen] = useState(false);

    const verPreview = async () => {
        setLoadingPreview(true);
        setResultado(null);

        try {
            const params = new URLSearchParams({
                spreadsheetUrl,
                gid,
            });
            const response = await fetch(`${baseURL}/sheetProductsPreview.php?${params.toString()}`);
            const data = await response.json();

            if (!response.ok || data.error) {
                throw new Error(data.error || 'No se pudo leer la hoja.');
            }

            setPreview(data);
            toast.success('Hoja revisada');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoadingPreview(false);
        }
    };

    const importar = async (modoImportacion = 'listos') => {
        const actualizarExistentes = modoImportacion === 'actualizarExistentes';
        const totalObjetivo = actualizarExistentes ? preview?.totalActualizables || preview?.totalYaImportados : preview?.totalListos;

        if (!totalObjetivo) {
            toast.error(actualizarExistentes ? 'No hay productos existentes para actualizar.' : 'No hay productos listos para publicar o actualizar.');
            return;
        }

        const confirmacion = await Swal.fire({
            title: actualizarExistentes ? 'Actualizar productos listos existentes?' : 'Publicar productos listos?',
            text: actualizarExistentes
                ? `Se actualizaran solo ${preview.totalYaImportados} productos que estan en Listo para publicar y ya existen por SKU o ID.`
                : `Se procesaran ${preview.totalListos} productos listos. Si alguno ya existe por ID o SKU, se actualiza sin duplicarlo.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#7c3aed',
            cancelButtonColor: '#d33',
            confirmButtonText: actualizarExistentes ? 'Actualizar listos' : 'Publicar listos',
            cancelButtonText: 'Cancelar',
        });

        if (!confirmacion.isConfirmed) {
            return;
        }

        setLoadingImport(true);

        try {
            const formData = new FormData();
            formData.append('spreadsheetUrl', spreadsheetUrl);
            formData.append('gid', gid);
            formData.append('actualizarExistentes', actualizarExistentes ? 'Si' : 'No');
            formData.append('modoImportacion', modoImportacion);

            const response = await fetch(`${baseURL}/sheetProductsImport.php`, {
                method: 'POST',
                body: formData,
            });
            const data = await response.json();

            if (!response.ok || data.error) {
                throw new Error(data.error || 'No se pudo importar.');
            }

            clearResourceCache('productos');
            clearResourceCache('categorias');
            clearResourceCache('subcategorias');
            clearResourceCache('secciones');
            setResultado(data);
            if (data.syncSheet?.ok) {
                toast.success(`${data.mensaje}. Sheet marcado como Publicado`);
            } else if (data.syncSheet?.skipped) {
                toast.success(data.mensaje);
            } else {
                toast.warn(`${data.mensaje}. No se pudo marcar el Sheet como Publicado`);
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoadingImport(false);
        }
    };

    return (
        <div className='sheetsImport'>
            <ToastContainer />
            <button className='sheetsImportToggle' onClick={() => setOpen(!open)}>
                <FontAwesomeIcon icon={faArrowDown} /> Importar Google Sheets
            </button>

            {open && (
                <div className='sheetsImportPanel'>
                    <div className='sheetsImportFields'>
                        <fieldset>
                            <legend>Google Sheets</legend>
                            <input
                                type='text'
                                value={spreadsheetUrl}
                                onChange={(e) => setSpreadsheetUrl(e.target.value)}
                            />
                        </fieldset>
                        <fieldset>
                            <legend>GID Productos</legend>
                            <input
                                type='text'
                                value={gid}
                                onChange={(e) => setGid(e.target.value)}
                            />
                        </fieldset>
                    </div>

                    <div className='sheetsImportActions'>
                        <button onClick={verPreview} className='btnPost' disabled={loadingPreview}>
                            <FontAwesomeIcon icon={loadingPreview ? faSync : faEye} /> {loadingPreview ? 'Revisando...' : 'Ver listos'}
                        </button>
                        <button onClick={() => importar('listos')} className='btnPost sheetsImportPrimary' disabled={loadingImport || !preview?.totalListos}>
                            <FontAwesomeIcon icon={loadingImport ? faSync : faArrowDown} /> {loadingImport ? 'Procesando...' : 'Publicar listos'}
                        </button>
                        <button onClick={() => importar('actualizarExistentes')} className='btnPost' disabled={loadingImport || !preview?.totalYaImportados}>
                            <FontAwesomeIcon icon={faSync} /> Actualizar listos existentes
                        </button>
                    </div>

                    {preview && (
                        <div className='sheetsImportSummary'>
                            <div>
                                <span>Total</span>
                                <strong>{preview.total}</strong>
                            </div>
                            <div>
                                <span>Listos</span>
                                <strong>{preview.totalListos}</strong>
                            </div>
                            <div>
                                <span>Nuevos</span>
                                <strong>{preview.totalNuevos || 0}</strong>
                            </div>
                            <div>
                                <span>Para actualizar</span>
                                <strong>{preview.totalActualizables || preview.totalYaImportados || 0}</strong>
                            </div>
                            <div>
                                <span>Pendientes</span>
                                <strong>{preview.totalPendientes}</strong>
                            </div>
                        </div>
                    )}

                    {preview?.listos?.length > 0 && (
                        <div className='sheetsImportTable'>
                            <h4>Listos para publicar o actualizar</h4>
                            {preview.listos.slice(0, 12).map(item => (
                                <div className='sheetsImportRow' key={`${item.rowNumber}-${item.sku}`}>
                                    <div>
                                        <strong>{item.titulo}</strong>
                                        <span>
                                            {item.seccion} / {item.categoria}{item.subcategoria ? ` / ${item.subcategoria}` : ''}
                                            {item.alreadyImported ? ` / actualiza ID ${item.existingProductId}` : ' / producto nuevo'}
                                        </span>
                                    </div>
                                    <div>
                                        <strong>${Number(item.precio || 0).toLocaleString('es-CO')}</strong>
                                        <span className='sheetOldPrice'>${Number(item.precioAnterior || 0).toLocaleString('es-CO')}</span>
                                        <span>Stock {item.stock} / Mas vendido {item.masVendido}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {preview?.yaImportados?.length > 0 && preview?.listos?.length === 0 && (
                        <div className='sheetsImportTable sheetsImportDone'>
                            <h4>Ya importados</h4>
                            {preview.yaImportados.slice(0, 12).map(item => (
                                <div className='sheetsImportRow' key={`${item.rowNumber}-${item.sku}-existente`}>
                                    <div>
                                        <strong>{item.titulo}</strong>
                                        <span>SKU {item.sku} ya existe como ID {item.existingProductId}. Al ponerlo Listo para publicar se actualiza sin duplicarlo.</span>
                                    </div>
                                    <div>
                                        <strong>ID {item.existingProductId}</strong>
                                        <span>Stock {item.stock} / Mas vendido {item.masVendido}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {preview?.pendientes?.length > 0 && (
                        <div className='sheetsImportTable sheetsImportPending'>
                            <h4>Pendientes</h4>
                            {preview.pendientes.slice(0, 8).map(item => (
                                <div className='sheetsImportRow' key={`${item.rowNumber}-${item.sku}-pendiente`}>
                                    <div>
                                        <strong>Fila {item.rowNumber}: {item.titulo || 'Sin producto'}</strong>
                                        <span>{item.errors.join(' | ')}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {resultado?.importados?.length > 0 && (
                        <div className='sheetsImportTable sheetsImportDone'>
                            <h4>Importados</h4>

                            {resultado.syncSheet && (
                                <div className='sheetsImportRow'>
                                    <div>
                                        <strong>
                                            {resultado.syncSheet.ok
                                                ? 'Sheet actualizado'
                                                : resultado.syncSheet.skipped
                                                    ? 'Sheet sin cambios'
                                                    : 'Sheet pendiente'}
                                        </strong>
                                        <span>
                                            {resultado.syncSheet.ok
                                                ? 'Los productos importados quedaron como Publicado en Google Sheets.'
                                                : resultado.syncSheet.message || 'No se pudo confirmar el cambio en Google Sheets.'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {resultado.importados.map(item => (
                                <div className='sheetsImportRow' key={`${item.rowNumber}-${item.idProducto}`}>
                                    <div>
                                        <strong>{item.titulo}</strong>
                                        <span>Fila {item.rowNumber} / {item.accion} / Stock {item.stock} / Mas vendido {item.masVendido}</span>
                                    </div>
                                    <div>
                                        <strong>ID {item.idProducto}</strong>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {resultado?.omitidos?.length > 0 && (
                        <div className='sheetsImportTable sheetsImportDone'>
                            <h4>Omitidos porque ya existian</h4>

                            {resultado.omitidos.map(item => (
                                <div className='sheetsImportRow' key={`${item.rowNumber}-${item.sku}-omitido`}>
                                    <div>
                                        <strong>{item.titulo}</strong>
                                        <span>SKU {item.sku} ya existe como ID {item.existingProductId}</span>
                                    </div>
                                    <div>
                                        <strong>ID {item.existingProductId}</strong>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

